/**
 * Shared General talent tree — data only.
 * Node list / ranks / per-rank values: M6 task brief (user-confirmed).
 * Milestone point schedule: NOT documented in store docs — do not invent.
 * Armour: +3% Armour per rank (M7-D1 confirmed; max 5 ranks → +15%).
 */

export type GeneralBranchId = 'offence' | 'defence' | 'dice';

export type GeneralTalentEffectKind =
  | 'attack_pct'
  | 'crit_damage_pct'
  | 'crit_chance_pp'
  | 'counterattack_chance_pp'
  | 'max_hp_pct'
  | 'armour_pct'
  | 'block_chance_pp'
  | 'dodge_chance_pp'
  | 'additional_dice_slots'
  | 'special_die_slot'
  | 'targeted_rerolls'
  | 'full_rerolls';

export interface GeneralTalentEffectDef {
  kind: GeneralTalentEffectKind;
  /** Per-rank magnitude; 0 when TBD / unlock flags. */
  perRank: number;
  description: string;
  openNote?: string;
}

export interface GeneralTalentNodeDef {
  id: string;
  name: string;
  branch: GeneralBranchId;
  maxRanks: number;
  effect: GeneralTalentEffectDef;
  /** Decorative layout in pan/zoom space. */
  layout: { x: number; y: number };
  decorativeParents?: readonly string[];
}

export interface GeneralBranchDef {
  id: GeneralBranchId;
  name: string;
}

/**
 * Point economy (confirmed where noted; milestone schedule OPEN).
 * Tree capacity (sum of maxRanks) = 61. Level cap 50.
 * Levels 2–50 → 49 points (same level−1 pattern as class).
 * Remaining 12 points are reserved milestone budget — schedule not in docs.
 */
export const GENERAL_TALENT_GATES = {
  levelCap: 50,
  /** Points from General levels 2–50. */
  maxLevelPoints: 49,
  /**
   * Reserved milestone / achievement-style budget so max total can reach 61.
   * OPEN: which milestones grant which amounts — do not grant until designed.
   */
  reservedMilestoneBudget: 12,
  /** Hard cap once milestones exist: 49 + 12. */
  maxTotalGeneralPoints: 61,
  /** Sum of all node maxRanks (Offence 25 + Defence 25 + Dice 11). */
  treeTotalRanks: 61,
} as const;

export const GENERAL_BRANCHES: readonly GeneralBranchDef[] = [
  { id: 'offence', name: 'Offence' },
  { id: 'defence', name: 'Defence' },
  { id: 'dice', name: 'Dice' },
];

/** Baseline combat budgets before General dice talents (shipped M3). */
export const GENERAL_DICE_BASELINES = {
  startingStandardDiceSlots: 1,
  maxStandardDiceSlots: 6,
  baseTargetedRerolls: 2,
  baseFullRerolls: 1,
} as const;

/**
 * Layout: three columns Offence | Defence | Dice.
 * Positions are decorative only — no purchase path gates.
 */
export const GENERAL_TALENT_NODES: readonly GeneralTalentNodeDef[] = [
  // —— Offence (25) ——
  {
    id: 'gen_off_attack',
    name: 'Attack',
    branch: 'offence',
    maxRanks: 10,
    effect: {
      kind: 'attack_pct',
      perRank: 2,
      description: '+2% weapon damage per rank',
    },
    layout: { x: 120, y: 80 },
  },
  {
    id: 'gen_off_crit_damage',
    name: 'Critical Damage',
    branch: 'offence',
    maxRanks: 5,
    effect: {
      kind: 'crit_damage_pct',
      perRank: 5,
      description: '+5% critical damage per rank',
    },
    layout: { x: 120, y: 180 },
    decorativeParents: ['gen_off_attack'],
  },
  {
    id: 'gen_off_crit_chance',
    name: 'Critical Chance',
    branch: 'offence',
    maxRanks: 5,
    effect: {
      kind: 'crit_chance_pp',
      perRank: 2,
      description: '+2 percentage points critical chance per rank',
    },
    layout: { x: 120, y: 280 },
    decorativeParents: ['gen_off_crit_damage'],
  },
  {
    id: 'gen_off_counter',
    name: 'Counterattack Chance',
    branch: 'offence',
    maxRanks: 5,
    effect: {
      kind: 'counterattack_chance_pp',
      perRank: 1,
      description: '+1 percentage point counterattack chance per rank',
    },
    layout: { x: 120, y: 380 },
    decorativeParents: ['gen_off_crit_chance'],
  },

  // —— Defence (25) ——
  {
    id: 'gen_def_health',
    name: 'Health',
    branch: 'defence',
    maxRanks: 10,
    effect: {
      kind: 'max_hp_pct',
      perRank: 3,
      description: '+3% max HP per rank',
    },
    layout: { x: 420, y: 80 },
  },
  {
    id: 'gen_def_armour',
    name: 'Armour',
    branch: 'defence',
    maxRanks: 5,
    effect: {
      kind: 'armour_pct',
      perRank: 3,
      description: '+3% Armour per rank (max +15%)',
    },
    layout: { x: 420, y: 180 },
    decorativeParents: ['gen_def_health'],
  },
  {
    id: 'gen_def_block',
    name: 'Block Chance',
    branch: 'defence',
    maxRanks: 5,
    effect: {
      kind: 'block_chance_pp',
      perRank: 1,
      description: '+1 percentage point block chance per rank',
    },
    layout: { x: 420, y: 280 },
    decorativeParents: ['gen_def_armour'],
  },
  {
    id: 'gen_def_dodge',
    name: 'Dodge Chance',
    branch: 'defence',
    maxRanks: 5,
    effect: {
      kind: 'dodge_chance_pp',
      perRank: 1,
      description: '+1 percentage point dodge chance per rank',
    },
    layout: { x: 420, y: 380 },
    decorativeParents: ['gen_def_block'],
  },

  // —— Dice (11) ——
  {
    id: 'gen_dice_slots',
    name: 'Additional Standard Dice Slots',
    branch: 'dice',
    maxRanks: 5,
    effect: {
      kind: 'additional_dice_slots',
      perRank: 1,
      description:
        '+1 permanent standard die slot per rank (start 1, max 6). Shared across classes.',
    },
    layout: { x: 720, y: 80 },
  },
  {
    id: 'gen_dice_special',
    name: 'Special Die Slot',
    branch: 'dice',
    maxRanks: 1,
    effect: {
      kind: 'special_die_slot',
      perRank: 1,
      description:
        'Unlocks a Special Die slot that replaces a standard die (does not add a seventh die).',
      openNote:
        'GDD: Special Die is an equipment modifier. Talent unlocks the slot; equip/combat effects deferred with gear (M7).',
    },
    layout: { x: 720, y: 180 },
    decorativeParents: ['gen_dice_slots'],
  },
  {
    id: 'gen_dice_targeted_rerolls',
    name: 'Targeted Rerolls',
    branch: 'dice',
    maxRanks: 3,
    effect: {
      kind: 'targeted_rerolls',
      perRank: 1,
      description: '+1 targeted single-die reroll per turn per rank (base 2)',
    },
    layout: { x: 720, y: 280 },
    decorativeParents: ['gen_dice_special'],
  },
  {
    id: 'gen_dice_full_rerolls',
    name: 'Full Rerolls',
    branch: 'dice',
    maxRanks: 2,
    effect: {
      kind: 'full_rerolls',
      perRank: 1,
      description: '+1 full reroll (Reroll All) per turn per rank (base 1)',
    },
    layout: { x: 720, y: 380 },
    decorativeParents: ['gen_dice_targeted_rerolls'],
  },
] as const;

const BY_ID = new Map(GENERAL_TALENT_NODES.map((n) => [n.id, n]));

export function getGeneralTalentNode(id: string): GeneralTalentNodeDef {
  const node = BY_ID.get(id);
  if (!node) {
    throw new Error(`Unknown General talent node: ${id}`);
  }
  return node;
}

export function listGeneralTalentNodes(
  branch?: GeneralBranchId,
): readonly GeneralTalentNodeDef[] {
  if (!branch) return GENERAL_TALENT_NODES;
  return GENERAL_TALENT_NODES.filter((n) => n.branch === branch);
}

export function generalTreeTotalMaxRanks(): number {
  return GENERAL_TALENT_NODES.reduce((sum, n) => sum + n.maxRanks, 0);
}
